import React, { useState, useEffect } from 'react';
import styles from './confirmation.module.css';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CONFIRMATION_TOKEN_KEY,
  NavigationPaths,
  TOKEN_KEY,
  TOKEN_KEY_NEW_PASS,
} from '../../constants';
import { AuthService } from '../../services';
import { Button, useNotification } from '../../components';
import { setAuthData } from '../../reducers/authReducer';
import { useDispatch } from 'react-redux';
import { AdminService } from '../../services/admin';

export const Confirmation = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isOnNewPasswordStep = localStorage.getItem('setNewPassword') as string;
  const [timer, setTimer] = useState(30);
  const [email, setEmail] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [code, setCode] = useState('');
  const [wrongCode, setWrongCode] = useState(false);
  const { addNotification } = useNotification();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    setEmail(decodeURIComponent(queryParams.get('email') || ''));
  }, [location.search]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const handleResendCode = async () => {
    if (isOnNewPasswordStep) {
      const { data: { data: { otc = undefined } = {} } = {} } =
        await AuthService.requestResetPassword(email);
      if (otc) {
        setCanResend(false);
        setTimer(30);
      }
    } else {
      const password = localStorage.getItem('pass') as string;
      const { data: { data: { tokenData: { accessToken = undefined } = {} } = {} } = {} } =
        await AuthService.auth(email, password);
      if (accessToken) {
        localStorage.setItem(TOKEN_KEY, accessToken);
        setCanResend(false);
        setTimer(30);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.toUpperCase());
  };

  const handleSubmit = async () => {
    if (code) {
      if (isOnNewPasswordStep) {
        const { data: { data: { tokenData: { accessToken = undefined } = {} } = {} } = {}, error } =
          await AuthService.requestResetPassword(email, code.toLowerCase());
        if (accessToken) {
          localStorage.setItem(TOKEN_KEY_NEW_PASS, accessToken);
          localStorage.removeItem('pass');
          return navigate(NavigationPaths.NEW_PASSWORD);
        } else if (error) {
          addNotification({
            title: t('error'),
            message: t(error.message || 'Error'),
            type: 'error',
          });
          return setWrongCode(true);
        }
      } else {
        const { data: { data: { tokenData: { accessToken = undefined } = {} } = {} } = {}, error } =
          await AuthService.confirmation(code.toLowerCase());
        if (accessToken) {
          localStorage.setItem(TOKEN_KEY, accessToken);
          localStorage.removeItem(CONFIRMATION_TOKEN_KEY);
          localStorage.removeItem('pass');
          dispatch(setAuthData(accessToken));
          await AdminService.setLanguage(localStorage.getItem('language') || 'ro');
          return navigate(NavigationPaths.ORGANISATION_USERS);
        } else if (error) {
          addNotification({
            title: t('error'),
            message: t(error.message || 'Error'),
            type: 'error',
          });
          return setWrongCode(true);
        }
      }
    }
    setWrongCode(true);
    return addNotification({
      title: t('error'),
      message: t('enterConfirmationCode'),
      type: 'error',
    });
  };

  const handleChangeEmail = () => {
    if (isOnNewPasswordStep) {
      navigate(NavigationPaths.RESET_PASSWORD);
    } else {
      navigate(NavigationPaths.LOGIN);
    }
  };

  const maskedEmail = email.replace(/(.{1}).+(@.+)/, '$1********$2');

  return (
    <div className={styles['confirmation-container']}>
      <div className={styles['confirmation-box']}>
        <div className={styles['confirmation-header']}>
          <h1 className={styles['confirmation-title']}>{t('confirmationCode')}</h1>
          <span className={styles['change-email']}>
            {t('wrongEmail')}{' '}
            <a
              href="#"
              onClick={handleChangeEmail}
            >
              {t('changeIt')}
            </a>
          </span>
        </div>

        <p className={styles['confirmation-description']}>
          {t('confirmationDescription')}
          <strong> {maskedEmail}</strong>
        </p>

        <input
          type="text"
          value={code}
          onChange={handleInputChange}
          className={`${styles['confirmation-input']} ${wrongCode && styles['error-input']}`}
          maxLength={6}
          placeholder="******"
        />

        <p className={styles['resend-timer']}>
          {canResend ? (
            <span
              className={styles['resend-link']}
              onClick={handleResendCode}
            >
              {t('resendCode')}
            </span>
          ) : (
            `${t('newCodeResend')}${timer.toString().padStart(2, '0')}`
          )}
        </p>

        <Button
          variant="primary"
          showArrow
          width="100%"
          onClick={handleSubmit}
        >
          {t('continue')}
        </Button>
      </div>
    </div>
  );
};
